'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Edit2, Trash2, Users, Mail, Phone } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { customers, customerGroups, territories } from '../data/mock-data';
import { formatCurrency } from '../../../types';
import type { Customer } from '../types';

// 고객 추가 모달
function CustomerAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="고객 추가"
      description="새로운 고객 정보를 입력합니다."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>저장</Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField label="고객코드" required>
          <FormInput placeholder="자동 생성" disabled />
        </FormField>
        <FormField label="고객명" required>
          <FormInput placeholder="고객명을 입력하세요" />
        </FormField>
        <FormField label="담당자">
          <FormInput placeholder="담당자명" />
        </FormField>
        <FormField label="이메일">
          <FormInput type="email" placeholder="example@email.com" />
        </FormField>
        <FormField label="전화번호">
          <FormInput placeholder="010-0000-0000" />
        </FormField>
        <FormField label="고객 그룹" required>
          <FormSelect>
            <option value="">선택하세요</option>
            {customerGroups.map((group) => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </FormSelect>
        </FormField>
        <FormField label="지역">
          <FormSelect>
            <option value="">선택하세요</option>
            {territories.map((territory) => (
              <option key={territory.id} value={territory.id}>{territory.name}</option>
            ))}
          </FormSelect>
        </FormField>
        <FormField label="여신한도">
          <FormInput type="number" placeholder="0" />
        </FormField>
        <div className="col-span-2">
          <FormField label="주소">
            <FormTextarea rows={2} placeholder="주소를 입력하세요" />
          </FormField>
        </div>
        <div className="col-span-2">
          <FormField label="비고">
            <FormTextarea rows={2} placeholder="추가 정보를 입력하세요" />
          </FormField>
        </div>
      </div>
    </Modal>
  );
}

export function CustomerListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [selectedTerritory, setSelectedTerritory] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return customers.filter((customer) => {
      // 검색 필터
      const matchesSearch =
        customer.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (customer.contactPerson && customer.contactPerson.toLowerCase().includes(searchQuery.toLowerCase()));

      // 그룹 필터
      const matchesGroup =
        selectedGroup === 'all' || customer.customerGroup === selectedGroup;

      // 지역 필터
      const matchesTerritory =
        selectedTerritory === 'all' || customer.territory === selectedTerritory;

      return matchesSearch && matchesGroup && matchesTerritory;
    });
  }, [searchQuery, selectedGroup, selectedTerritory]);

  const columns = [
    {
      key: 'code',
      label: '고객코드',
      width: '8%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm">{value as string}</span>
      ),
    },
    {
      key: 'name',
      label: '고객명',
      width: '18%',
      sortable: true,
      render: (value: unknown, row: Customer) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
            <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{value as string}</div>
            {row.contactPerson && (
              <div className="text-xs text-muted-foreground truncate">
                담당: {row.contactPerson}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'contact',
      label: '연락처',
      width: '18%',
      render: (_: unknown, row: Customer) => (
        <div className="space-y-0.5">
          {row.email && (
            <div className="flex items-center gap-1 text-xs">
              <Mail className="w-3 h-3 text-muted-foreground flex-shrink-0" />
              <span className="truncate">{row.email}</span>
            </div>
          )}
          {row.phone && (
            <div className="flex items-center gap-1 text-xs">
              <Phone className="w-3 h-3 text-muted-foreground flex-shrink-0" />
              <span className="whitespace-nowrap">{row.phone}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'customerGroup',
      label: '그룹',
      width: '10%',
      render: (value: unknown) => {
        const group = customerGroups.find((g) => g.id === value);
        return <span className="whitespace-nowrap">{group?.name || String(value)}</span>;
      },
    },
    {
      key: 'territory',
      label: '지역',
      width: '8%',
      render: (value: unknown) => {
        const territory = territories.find((t) => t.id === value);
        return <span className="whitespace-nowrap">{territory?.name || (value ? String(value) : '-')}</span>;
      },
    },
    {
      key: 'creditLimit',
      label: '여신한도',
      width: '12%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown) => (
        <span className="whitespace-nowrap">{formatCurrency(value as number)}</span>
      ),
    },
    {
      key: 'outstandingAmount',
      label: '미수금',
      width: '12%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown, row: Customer) => {
        const amount = value as number;
        const isOverLimit = amount > row.creditLimit * 0.8;
        return (
          <span className={`whitespace-nowrap ${isOverLimit ? 'text-red-500 font-medium' : ''}`}>
            {formatCurrency(amount)}
          </span>
        );
      },
    },
    {
      key: 'isActive',
      label: '상태',
      width: '7%',
      render: (value: unknown) => (
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
            value
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
          }`}
        >
          {value ? '활성' : '비활성'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '7%',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="수정">
            <Edit2 className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="삭제">
            <Trash2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  // 요약 정보
  const totalCustomers = customers.length;
  const activeCustomers = customers.filter((c) => c.isActive).length;
  const totalOutstanding = customers.reduce((sum, c) => sum + c.outstandingAmount, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="고객 관리"
        description="거래처 및 고객 정보를 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            고객 추가
          </button>
        }
      />

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">전체 고객</div>
          <div className="text-2xl font-bold mt-1">
            {totalCustomers}개
            <span className="text-sm font-normal text-muted-foreground ml-2">
              (활성 {activeCustomers}개)
            </span>
          </div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">총 미수금</div>
          <div className="text-2xl font-bold mt-1 text-yellow-500">
            {formatCurrency(totalOutstanding)}
          </div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">고객 그룹</div>
          <div className="text-2xl font-bold mt-1">
            {customerGroups.length}개
          </div>
        </div>
      </div>

      {/* 필터 영역 */}
      <div className="flex flex-wrap gap-4">
        {/* 검색 */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="고객코드, 고객명, 담당자 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* 그룹 필터 */}
        <select
          value={selectedGroup}
          onChange={(e) => setSelectedGroup(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 그룹</option>
          {customerGroups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name} ({group.customerCount})
            </option>
          ))}
        </select>

        {/* 지역 필터 */}
        <select
          value={selectedTerritory}
          onChange={(e) => setSelectedTerritory(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 지역</option>
          {territories.map((territory) => (
            <option key={territory.id} value={territory.id}>
              {territory.name} ({territory.customerCount})
            </option>
          ))}
        </select>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 고객 추가 모달 */}
      <CustomerAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
